import { useState, useRef, useCallback } from "react";

interface UseCallRecordingReturn {
  isRecording: boolean;
  recordingDuration: number;
  hasConsent: boolean;
  recordedBlob: Blob | null;
  startRecording: (remoteStream: MediaStream, localStream: MediaStream) => void;
  stopRecording: () => void;
  requestConsent: () => void;
  grantConsent: () => void;
  denyConsent: () => void;
  resetRecording: () => void;
}

export const useCallRecording = (): UseCallRecordingReturn => {
  const [isRecording, setIsRecording] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [hasConsent, setHasConsent] = useState(false);
  const [showConsentRequest, setShowConsentRequest] = useState(false);
  const [recordedBlob, setRecordedBlob] = useState<Blob | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const combinedStreamRef = useRef<MediaStream | null>(null);

  const startRecording = useCallback((remoteStream: MediaStream, localStream: MediaStream) => {
    if (!hasConsent) {
      console.warn("Cannot start recording without consent");
      return;
    }

    try {
      // Create audio context to mix both audio streams
      const audioContext = new AudioContext();
      const destination = audioContext.createMediaStreamDestination();

      // Add remote audio
      const remoteAudioTracks = remoteStream.getAudioTracks();
      if (remoteAudioTracks.length > 0) {
        const remoteSource = audioContext.createMediaStreamSource(new MediaStream(remoteAudioTracks));
        remoteSource.connect(destination);
      }

      // Add local audio
      const localAudioTracks = localStream.getAudioTracks();
      if (localAudioTracks.length > 0) {
        const localSource = audioContext.createMediaStreamSource(new MediaStream(localAudioTracks));
        localSource.connect(destination);
      }

      // Combine video from remote with mixed audio
      const videoTrack = remoteStream.getVideoTracks()[0];
      const combinedStream = new MediaStream([
        ...(videoTrack ? [videoTrack] : []),
        ...destination.stream.getAudioTracks(),
      ]);
      combinedStreamRef.current = combinedStream;

      // Set up MediaRecorder
      const mimeType = MediaRecorder.isTypeSupported("video/webm;codecs=vp9")
        ? "video/webm;codecs=vp9"
        : MediaRecorder.isTypeSupported("video/webm")
        ? "video/webm"
        : "video/mp4";

      const mediaRecorder = new MediaRecorder(combinedStream, {
        mimeType,
        videoBitsPerSecond: 2500000,
      });

      chunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mimeType });
        setRecordedBlob(blob);
        chunksRef.current = [];
      };

      mediaRecorder.start(1000); // Collect data every second
      mediaRecorderRef.current = mediaRecorder;
      setIsRecording(true);
      setRecordingDuration(0);

      // Start duration timer
      timerRef.current = setInterval(() => {
        setRecordingDuration((prev) => prev + 1);
      }, 1000);

      // Haptic feedback
      if ("vibrate" in navigator) {
        navigator.vibrate(50);
      }

      console.log("Call recording started");
    } catch (error) {
      console.error("Error starting call recording:", error);
    }
  }, [hasConsent]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current = null;
      setIsRecording(false);

      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }

      // Haptic feedback
      if ("vibrate" in navigator) {
        navigator.vibrate([50, 50, 50]);
      }

      console.log("Call recording stopped");
    }
  }, [isRecording]);

  const requestConsent = useCallback(() => {
    setShowConsentRequest(true);
  }, []);

  const grantConsent = useCallback(() => {
    setHasConsent(true);
    setShowConsentRequest(false);
  }, []);

  const denyConsent = useCallback(() => {
    setHasConsent(false);
    setShowConsentRequest(false);
  }, []);

  const resetRecording = useCallback(() => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current = null;
    }
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setIsRecording(false);
    setRecordingDuration(0);
    setHasConsent(false);
    setRecordedBlob(null);
    chunksRef.current = [];
  }, []);

  return {
    isRecording,
    recordingDuration,
    hasConsent,
    recordedBlob,
    startRecording,
    stopRecording,
    requestConsent,
    grantConsent,
    denyConsent,
    resetRecording,
  };
};
